import React from 'react';
import type { AgentConfig } from '../../../../services/authService';
import authService from '../../../../services/authService';

export const useStoreDetailsOverviewAgentConfig = (storeId?: string, shouldLoad = true) => {
    const [agentConfig, setAgentConfig] = React.useState<AgentConfig | null>(null);
    const [agentConfigLoading, setAgentConfigLoading] = React.useState(false);
    const activeStoreIdRef = React.useRef(storeId);
    activeStoreIdRef.current = storeId;

    const loadAgentConfig = React.useCallback(async (showLoader = true) => {
        if (!storeId || !shouldLoad) {
            return null;
        }

        try {
            if (showLoader) {
                setAgentConfigLoading(true);
            }
            const config = await authService.getAgentConfig(storeId);
            setAgentConfig(config);
            return config;
        } catch (error) {
            console.error('Error loading agent config:', error);
            if (showLoader) {
                setAgentConfig(null);
            }
            return null;
        } finally {
            if (showLoader) {
                setAgentConfigLoading(false);
            }
        }
    }, [shouldLoad, storeId]);

    React.useEffect(() => {
        setAgentConfig(null);
    }, [storeId]);

    React.useEffect(() => {
        if (!shouldLoad || !storeId) {
            return;
        }

        void loadAgentConfig();
    }, [loadAgentConfig, shouldLoad, storeId]);

    const saveAgentConfig = React.useCallback(async (payload: Record<string, unknown>) => {
        if (!storeId) throw new Error('Store ID is missing');
        const response = await authService.saveAgentConfig(storeId, payload);
        if (activeStoreIdRef.current !== storeId) return;
        const saved: AgentConfig | null = response?.config ? response : null;
        setAgentConfig((current) => {
            const base = saved ?? current;
            return base ? {
                ...current,
                ...base,
                config: { ...current?.config, ...base.config, ...payload, ...saved?.config },
            } : current;
        });
    }, [storeId]);

    const refreshAgentConfig = React.useCallback(async () => {
        await loadAgentConfig(true);
    }, [loadAgentConfig]);

    const refreshAgentConfigSilently = React.useCallback(async () => {
        await loadAgentConfig(false);
    }, [loadAgentConfig]);

    return {
        agentConfig,
        agentConfigLoading,
        saveAgentConfig,
        refreshAgentConfig,
        refreshAgentConfigSilently,
    };
};
